import { type AnyRouter, getRouterContext } from '@tanstack/react-router';
import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';

import {
  clearSearchFocus,
  clearSearchFocusFromLocation,
  parseSearchFocus,
  parseSearchFromLocation,
  type SearchFocus,
} from '@/lib/search-focus';

const SEARCH_FOCUS_LOCATION_CHANGE_EVENT = 'search-focus:locationchange';

type PatchableHistory = History & {
  __searchFocusPatchCount__?: number;
  __searchFocusOriginalPushState__?: History['pushState'];
  __searchFocusOriginalReplaceState__?: History['replaceState'];
};

function notifySearchFocusLocationChange() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(SEARCH_FOCUS_LOCATION_CHANGE_EVENT));
}

function installSearchFocusHistoryPatch(): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const history = window.history as PatchableHistory;
  history.__searchFocusPatchCount__ =
    (history.__searchFocusPatchCount__ ?? 0) + 1;

  if (!history.__searchFocusOriginalPushState__) {
    history.__searchFocusOriginalPushState__ = history.pushState.bind(history);
    history.pushState = ((...args: Parameters<History['pushState']>) => {
      const previousHref = window.location.href;
      history.__searchFocusOriginalPushState__?.(...args);

      if (window.location.href !== previousHref) {
        notifySearchFocusLocationChange();
      }
    }) as History['pushState'];
  }

  if (!history.__searchFocusOriginalReplaceState__) {
    history.__searchFocusOriginalReplaceState__ =
      history.replaceState.bind(history);
    history.replaceState = ((...args: Parameters<History['replaceState']>) => {
      const previousHref = window.location.href;
      history.__searchFocusOriginalReplaceState__?.(...args);

      if (window.location.href !== previousHref) {
        notifySearchFocusLocationChange();
      }
    }) as History['replaceState'];
  }

  return () => {
    const currentCount = history.__searchFocusPatchCount__ ?? 1;
    const nextCount = currentCount - 1;

    if (nextCount > 0) {
      history.__searchFocusPatchCount__ = nextCount;
      return;
    }

    if (history.__searchFocusOriginalPushState__) {
      history.pushState = history.__searchFocusOriginalPushState__;
      delete history.__searchFocusOriginalPushState__;
    }

    if (history.__searchFocusOriginalReplaceState__) {
      history.replaceState = history.__searchFocusOriginalReplaceState__;
      delete history.__searchFocusOriginalReplaceState__;
    }

    delete history.__searchFocusPatchCount__;
  };
}

type UseSearchFocusStateOptions =
  | { editorId: string; threadId?: undefined }
  | { threadId: string; editorId?: undefined }
  | undefined;

interface UseSearchFocusStateResult {
  searchFocus: SearchFocus;
  clearSearchFocusParams: () => void;
}

export function useSearchFocusState(
  options?: UseSearchFocusStateOptions
): UseSearchFocusStateResult {
  const router = useContext(getRouterContext()) as AnyRouter | undefined;
  const fallbackSnapshotRef = useRef<{
    href: string;
    snapshot: Record<string, unknown>;
  }>({
    href: '',
    snapshot: {},
  });

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!router) {
        if (typeof window === 'undefined') {
          return () => {};
        }

        const notify = () => onStoreChange();
        const uninstallHistoryPatch = installSearchFocusHistoryPatch();

        window.addEventListener('popstate', notify);
        window.addEventListener('hashchange', notify);
        window.addEventListener(SEARCH_FOCUS_LOCATION_CHANGE_EVENT, notify);

        return () => {
          window.removeEventListener('popstate', notify);
          window.removeEventListener('hashchange', notify);
          window.removeEventListener(
            SEARCH_FOCUS_LOCATION_CHANGE_EVENT,
            notify
          );
          uninstallHistoryPatch();
        };
      }

      const notify = () => onStoreChange();
      const unsubscribeBeforeNavigate = router.subscribe(
        'onBeforeNavigate',
        notify
      );
      const unsubscribeResolved = router.subscribe('onResolved', notify);

      return () => {
        unsubscribeBeforeNavigate();
        unsubscribeResolved();
      };
    },
    [router]
  );

  const getSnapshot = useCallback(() => {
    if (router) {
      return (router.state.location.search ?? {}) as Record<string, unknown>;
    }

    if (typeof window === 'undefined') {
      return {};
    }

    const href = window.location.href;
    if (fallbackSnapshotRef.current.href === href) {
      return fallbackSnapshotRef.current.snapshot;
    }

    const snapshot = parseSearchFromLocation();
    fallbackSnapshotRef.current = { href, snapshot };
    return snapshot;
  }, [router]);

  const rawSearch = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const searchFocus = useMemo(
    () => parseSearchFocus(rawSearch ?? {}),
    [rawSearch]
  );

  const matchedSearchFocus = useMemo<SearchFocus>(() => {
    if (!searchFocus.focusQuery) {
      return {};
    }

    const hasEditorTarget = Boolean(searchFocus.focusEditorId);
    const hasThreadTarget = Boolean(searchFocus.focusThreadId);

    if (hasEditorTarget && hasThreadTarget) {
      return {};
    }

    if (hasEditorTarget) {
      if (options?.editorId === searchFocus.focusEditorId) {
        return searchFocus;
      }

      return {};
    }

    if (hasThreadTarget) {
      if (options?.threadId === searchFocus.focusThreadId) {
        return searchFocus;
      }

      return {};
    }

    if (options?.editorId || options?.threadId) {
      return {};
    }

    return searchFocus;
  }, [options?.editorId, options?.threadId, searchFocus]);

  const clearSearchFocusParams = useCallback(() => {
    const currentRawSearch = router
      ? ((router.state.location.search ?? {}) as Record<string, unknown>)
      : parseSearchFromLocation();

    const currentSearchFocus = parseSearchFocus(currentRawSearch);

    const hasFocusParams =
      currentSearchFocus.focusQuery !== undefined ||
      currentSearchFocus.focusBucketId !== undefined ||
      currentSearchFocus.focusEditorId !== undefined ||
      currentSearchFocus.focusThreadId !== undefined ||
      currentSearchFocus.focusOccurrence !== undefined;

    if (!hasFocusParams) {
      return;
    }

    const hasEditorTarget = Boolean(
      currentSearchFocus.focusQuery && currentSearchFocus.focusEditorId
    );
    const hasThreadTarget = Boolean(
      currentSearchFocus.focusQuery && currentSearchFocus.focusThreadId
    );

    if (hasEditorTarget && hasThreadTarget) {
      return;
    }

    if (hasEditorTarget) {
      if (options?.editorId !== currentSearchFocus.focusEditorId) {
        return;
      }
    } else if (hasThreadTarget) {
      if (options?.threadId !== currentSearchFocus.focusThreadId) {
        return;
      }
    } else if (options?.editorId || options?.threadId) {
      return;
    }

    if (router) {
      void router.navigate({
        to: router.state.location.pathname,
        hash: router.state.location.hash,
        search: (previous: Record<string, unknown>) =>
          clearSearchFocus(previous as Record<string, unknown>),
        replace: true,
      });
      return;
    }

    clearSearchFocusFromLocation();
  }, [options?.editorId, options?.threadId, router]);

  return {
    clearSearchFocusParams,
    searchFocus: matchedSearchFocus,
  };
}
